import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import NewCustomerPage from "@/pages/NewCustomerPage";
import SavingsPage from "@/pages/SavingsPage";
import SavingsDetailPage from "@/pages/SavingsDetailPage";
import LoansPage from "@/pages/LoansPage";
import LoanDetailPage from "@/pages/LoanDetailPage";
import LoanClosurePage from "@/pages/LoanClosurePage";
import MaturityPage from "@/pages/MaturityPage";
import CollectionsPage from "@/pages/CollectionsPage";
import ExpensesPage from "@/pages/ExpensesPage";
import EditRequestsPage from "@/pages/EditRequestsPage";
import SettingsPage from "@/pages/SettingsPage";
import BranchesPage from "@/pages/BranchesPage";
import UsersPage from "@/pages/UsersPage";
import PermissionsPage from "@/pages/PermissionsPage";
import AgentCollectPage from "@/pages/AgentCollectPage";
import ReportsPage from "@/pages/ReportsPage";
import LoanRequestsPage from "@/pages/LoanRequestsPage";
import ResetPage from "@/pages/ResetPage";

function AppRouter() {
  const location = useLocation();
  // Emergent Auth returns to us with #session_id=... in the URL fragment.
  // Handle it BEFORE normal routing (which runs ProtectedRoute + AuthProvider bootstrap).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Full-screen mobile route (no sidebar) */}
      <Route
        path="/agent/collect"
        element={
          <ProtectedRoute roles={["super_admin", "branch_admin", "agent"]}>
            <AgentCollectPage />
          </ProtectedRoute>
        }
      />
      {/* App shell */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/new" element={<NewCustomerPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/savings" element={<ProtectedRoute roles={["super_admin","branch_admin","staff"]}><SavingsPage /></ProtectedRoute>} />
        <Route path="/savings/maturity" element={<ProtectedRoute roles={["super_admin","branch_admin","staff"]}><MaturityPage /></ProtectedRoute>} />
        <Route path="/savings/:id" element={<ProtectedRoute roles={["super_admin","branch_admin","staff","agent"]}><SavingsDetailPage /></ProtectedRoute>} />
        <Route path="/loans" element={<ProtectedRoute roles={["super_admin","branch_admin","staff"]}><LoansPage /></ProtectedRoute>} />
        <Route path="/loans/closure" element={<ProtectedRoute roles={["super_admin","branch_admin","staff"]}><LoanClosurePage /></ProtectedRoute>} />
        <Route path="/loans/:id" element={<ProtectedRoute roles={["super_admin","branch_admin","staff","agent"]}><LoanDetailPage /></ProtectedRoute>} />
        <Route path="/collections" element={<CollectionsPage />} />
        <Route path="/expenses" element={<ProtectedRoute roles={["super_admin","branch_admin","staff"]}><ExpensesPage /></ProtectedRoute>} />
        <Route path="/edit-requests" element={<EditRequestsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/branches" element={<ProtectedRoute roles={["super_admin"]}><BranchesPage /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute roles={["super_admin","branch_admin"]}><UsersPage /></ProtectedRoute>} />
        <Route path="/permissions" element={<PermissionsPage />} />
        <Route path="/reports" element={<ProtectedRoute roles={["super_admin","branch_admin","staff"]}><ReportsPage /></ProtectedRoute>} />
        <Route path="/loan-requests" element={<LoanRequestsPage />} />
        <Route path="/reset" element={<ProtectedRoute roles={["super_admin"]}><ResetPage /></ProtectedRoute>} />
      </Route>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
